import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysDeliveryNote } from './queryKeys';

type UpdateArticleTemplateResponse = {
  code: number;
  id: UUID;
  requestId: string;
};

type UpdateArticleTemplatePayload = {
  articles: unknown[];
  companyId: UUID;
};

/**
 * Updates an article template.
 *
 * @param {UpdateArticleTemplatePayload} articleTemplateData - An object containing the article template details to be updated.
 * @returns {Promise<UpdateArticleTemplatePayload>} - A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Article-Master/operation/create_or_upload_article_article_master_put
 */
export const updateArticleTemplate = async (
  articleTemplateData: UpdateArticleTemplatePayload,
) => {
  const { companyId, articles } = articleTemplateData;
  const response = await vestigasApi
    .put(ENDPOINT.DELIVERY_NOTE.UPDATE_ARTICLE_TEMPLATE(), {
      json: articles,
      searchParams: { companyId },
    })
    .json<UpdateArticleTemplateResponse>();

  return response;
};

/**
 * Custom hook for updating an article template using mutations.
 *
 * @returns {Object} An object containing the mutation function and other properties.
 */
export const useMutationUpdateArticleTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (articleTemplateData: UpdateArticleTemplatePayload) =>
      updateArticleTemplate(articleTemplateData),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeysDeliveryNote.getAllArticleTemplates({}),
      });
    },
  });
};
